import { Dropdown } from 'antd'
import React, { useCallback } from 'react'
import { FilterType } from '../../../modules/TodoFilter'
import { PRIORITY, TODO_STATUS, TodoItem } from '../../../modules/TodoItem'
import { TodoRenderDataItem } from '../types'
import TodoListItem from '../TodoListItem'
import styles from './style.module.scss'

interface TodoItemWrapperProps {
  item: TodoRenderDataItem
  selectedTodo: TodoItem | null
  currentFilter: FilterType
  onTodoSelect: (todo: TodoItem | null) => void
  onImportantToggle: (todo: TodoItem, important: boolean) => void
  onCompleteToggle: (todo: TodoItem, completed: boolean) => void
  onTodoUpdate: () => void
  searchText: string
  timeFormat: string
  dateFormat: string
}

const TodoItemWrapper: React.FC<TodoItemWrapperProps> = ({
  item,
  selectedTodo,
  currentFilter,
  onTodoSelect,
  onImportantToggle,
  onCompleteToggle,
  onTodoUpdate,
  searchText,
  timeFormat,
  dateFormat,
}) => {
  const { todo, folderData, contextMenuItems, datePickerCallbacks } = item
  if (!todo) return null

  // Derived values
  const isReadOnly = folderData?.isReadOnly ?? false
  const isSelected = selectedTodo?.url === todo.url
  const isImportant = todo.priority === PRIORITY.HIGH
  const isCompleted = todo.status === TODO_STATUS.COMPLETED
  const hideSubInfo = currentFilter === FilterType.FocusDate

  // Memoized handlers
  const handleTodoSelect = useCallback(() => {
    onTodoSelect(todo)
  }, [onTodoSelect, todo])

  const handleImportantToggle = useCallback(() => {
    onImportantToggle(todo, !isImportant)
  }, [onImportantToggle, todo, isImportant])

  const handleCompleteToggle = useCallback(
    (checked: boolean) => {
      onCompleteToggle(todo, checked)
    },
    [onCompleteToggle, todo],
  )

  const todoItemContent = (
    <div
      key={todo.uid}
      className={[styles.todoListItemWrapper, isSelected ? styles.selected : ''].join(' ')}
    >
      <TodoListItem
        summary={todo.summary}
        folderName={folderData?.displayName}
        dueDate={todo.dueDate}
        startDate={todo.startDate}
        reminders={todo.alarms}
        recurrenceRule={todo.recurrenceRule}
        description={todo.description}
        important={isImportant}
        completed={isCompleted}
        folderColor={folderData?.color}
        isSelected={isSelected}
        hideSubInfo={hideSubInfo}
        taskInstance={todo}
        isReadOnly={isReadOnly}
        onClick={handleTodoSelect}
        onImportantClick={handleImportantToggle}
        onCompleteToggle={handleCompleteToggle}
        onTodoUpdate={onTodoUpdate}
        onOpenDatePicker={datePickerCallbacks?.onOpenDatePicker}
        onOpenRecurrenceEditor={datePickerCallbacks?.onOpenRecurrenceEditor}
        searchText={searchText}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
      />
    </div>
  )

  return (
    <Dropdown
      menu={{ items: contextMenuItems }}
      trigger={isReadOnly ? [] : ['contextMenu']}
      destroyOnHidden
      overlayClassName="contextMenu"
    >
      {todoItemContent}
    </Dropdown>
  )
}

export default TodoItemWrapper
